import { fetchReaderBook, getCachedReaderBook, type ReaderBook } from '@/services/booksApi';
import { analytics } from '@/services/analytics';
import {
  getOfflineSettings,
  subscribeToOfflineSettings,
  type OfflineSettings,
  type PinnedBook,
} from '@/services/offlineSettings';
import { readCachedCatalog } from '@/services/readerCacheStore';
import { getAudioProgressForBook, isBookFinished } from '@/utils/audioProgressUtils';
import { collectChapterMediaUrls } from '@/utils/chapterMedia';
import {
  getNetworkState,
  isUnmetered,
  subscribeToNetworkState,
  type ConnectionType,
  type NetworkState,
} from '@/utils/networkState';
import { resolveAssetUrl } from '@/utils/offlineAssets';
import { isNativeApp } from '@/utils/platform';
import { listChapterAssets, type ChapterAssetKind } from '@/utils/chapterAssets';
import {
  getStoreUsage,
  hasAsset,
  isAssetStoreError,
  listStoredAssets,
  purgeBook,
  putAsset,
  subscribeToAssetProgress,
  type AssetProgress,
  type PurgeOptions,
  type PutAssetOptions,
  type StoredAsset,
  type StoreUsage,
} from './assetStore';
import { bufferedAheadMs, lookAheadWindowMs, planBufferFetches, type PlannedFetch } from './bufferPolicy';

/**
 * The look-ahead buffer: keeps the next stretch of the book on disk while the
 * user listens, quietly, and never asks. And, behind it, the explicit side:
 * books the user has pinned are fetched whole.
 *
 * All the judgement lives in `bufferPolicy`; this is the loop around it.
 * Each step it asks the policy what should be fetched next given where the
 * playhead is and what kind of connection there is, fetches exactly one
 * asset, and asks again. Re-planning before every fetch is what makes a TOC
 * jump or a Wi-Fi-to-cellular switch harmless: the next plan starts from the
 * new situation, and the most that is wasted is the one download already in
 * flight, because the transfer plugin cannot cancel one.
 *
 * Pinned books are a second source of work for the same loop, not a second
 * loop. The open book's plan always comes first, so listening never waits on
 * a download; when it is empty, the first pinned book with anything missing
 * is planned with an infinite window, which the policy already supports, and
 * fetched one asset at a time like everything else. Because the plan is
 * recomputed from what the store holds, a download resumes after a kill or a
 * relaunch for free, and progress is derived rather than stored. A pinned
 * book is fetched in full over cellular too, when the mobile-data switch
 * allows anything at all: the user asked for the whole book and saw its size.
 *
 * That same fact is why there is a watchdog. One download on the device pass
 * hung without ever failing; with no cancel, the only defence is to notice
 * that no bytes have arrived for a while, give up on that asset for the
 * session, and move on.
 *
 * The cap is the store's to report and this loop's to act on: evict the
 * least-recently-played *finished* book, and if there is none, stop fetching
 * ahead and let the reader stream. Nothing in progress is ever deleted, and
 * a pinned book is never a victim, finished or not.
 *
 * There is no depth indicator in the reader by design, so everything this
 * loop decides is written to a log the dev card can show and to analytics,
 * because "why did it stop" is otherwise unanswerable.
 */

export interface BufferInputs {
  book: ReaderBook | null;
  voiceKey: string;
  currentChapterIndex: number;
  /** Playhead within the current chapter. */
  positionInChapterMs: number;
  isPlaying: boolean;
}

export type BufferState =
  /** No book open and nothing pinned, or not on native. */
  | 'idle'
  | 'fetching'
  | 'offline'
  /** On cellular, and the user has said not to spend mobile data. */
  | 'cellular-off'
  /** Everything wanted — the open book's window and every pinned book — is on disk. */
  | 'window-full'
  /** The store is full and nothing finished can be evicted. Streaming. */
  | 'cap-reached';

export interface BufferInFlight {
  cdnUrl: string;
  kind: ChapterAssetKind;
  bookId: string;
  chapterIndex: number;
  startedAt: number;
}

export interface PinnedBookProgress {
  bookId: string;
  /** The voice being held: the pin's, or the book's default if that voice is gone. */
  voiceKey: string;
  /** Chapters whose every asset, discovered images included, is on disk. */
  chaptersDone: number;
  chaptersTotal: number;
  /** Bytes the store holds for this book. */
  bytes: number;
  /** Nothing left to fetch, as far as is known. */
  complete: boolean;
  /**
   * Assets given up on this session. The book cannot complete until they
   * are tried again: next session, or when the pins change.
   */
  blocked: number;
}

export interface BufferStatus {
  state: BufferState;
  /** The open book, if any. */
  bookId: string | null;
  connectionType: ConnectionType;
  windowMs: number;
  bufferedAheadMs: number;
  /** Fetches the last plan still wanted, including the one in flight. */
  queued: number;
  inFlight: BufferInFlight | null;
  evictions: number;
  failures: number;
  /**
   * Per pinned book, in pin order, whenever its payload is at hand. A pinned
   * book whose payload has not been obtained yet has no entry.
   */
  pinned: readonly PinnedBookProgress[];
  /** Newest first, with a time of day on each line. */
  log: readonly string[];
}

export interface BufferSchedulerDeps {
  isNative: () => boolean;
  store: {
    putAsset: (options: PutAssetOptions) => Promise<StoredAsset>;
    hasAsset: (cdnUrl: string) => boolean;
    getStoreUsage: () => StoreUsage;
    listStoredAssets: () => StoredAsset[];
    purgeBook: (bookId: string, options?: PurgeOptions) => Promise<void>;
    subscribeToAssetProgress: (listener: (progress: AssetProgress) => void) => () => void;
  };
  network: {
    getNetworkState: () => Promise<NetworkState>;
    subscribeToNetworkState: (onChange: (state: NetworkState) => void) => () => void;
  };
  settings: {
    getOfflineSettings: () => OfflineSettings;
    subscribeToOfflineSettings: (onChange: () => void) => () => void;
  };
  /** Reader payloads for pinned books, which the player is not holding. */
  readerBooks: {
    getCached: (bookId: string) => ReaderBook | null;
    fetch: (bookId: string) => Promise<ReaderBook>;
  };
  /** The text of a held asset, or null if it is not held or cannot be read. */
  readHeldText: (cdnUrl: string) => Promise<string | null>;
  isBookFinished: (bookId: string) => boolean;
  lastPlayedAt: (bookId: string) => number;
  track: (event: string, properties: Record<string, unknown>) => void;
  now: () => number;
  timers: {
    set: (callback: () => void, ms: number) => unknown;
    clear: (handle: unknown) => void;
  };
  /** No bytes for this long and a download is treated as hung. */
  watchdogMs: number;
  /** How often to re-plan while playing, so the window rolls with the playhead. */
  tickMs: number;
}

export interface BufferScheduler {
  /** The current book, voice and playhead. Cheap; call on every change. */
  update: (inputs: BufferInputs) => void;
  /** Re-plan now, for example after the user purged storage. */
  poke: () => void;
  /**
   * The files the player and the reader are using right now, which a purge
   * must leave alone: everything of the open book's current chapter, and
   * the text of the chapters the reader keeps around it. Empty with no
   * book open. See `PurgeOptions.keepUrls` for why.
   */
  inUseAssetUrls: () => string[];
  getStatus: () => BufferStatus;
  subscribe: (listener: (status: BufferStatus) => void) => () => void;
  /** Tear down. Used by tests and by a book being closed for good. */
  stop: () => void;
}

const LOG_LINES = 60;

/**
 * How far either side of the current chapter the reader may have a chapter
 * loaded: its prefetch radius (`PREFETCH_RADIUS` in useChapterFragments).
 * Those fragments already carry local URLs for their images.
 */
const IN_USE_CHAPTER_RADIUS = 2;

const timeOfDay = (epochMs: number): string => new Date(epochMs).toLocaleTimeString([], { hour12: false });

const formatBytes = (bytes: number): string =>
  bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

const errorText = (error: unknown): string => (error instanceof Error ? error.message : String(error));

const pinsKey = (settings: OfflineSettings): string =>
  settings.pinnedBooks.map((pin) => `${pin.bookId}:${pin.voiceKey}`).join('|');

type FetchOutcome =
  | { kind: 'stored'; asset: StoredAsset }
  | { kind: 'error'; error: unknown }
  | { kind: 'stalled' };

/** What the loop has decided to fetch from next, and on whose behalf. */
interface Work {
  book: ReaderBook;
  plan: PlannedFetch[];
  /** Null when this is the open book's own window. */
  pin: PinnedBook | null;
}

export const createBufferScheduler = (deps: BufferSchedulerDeps): BufferScheduler => {
  let inputs: BufferInputs = { book: null, voiceKey: '', currentChapterIndex: 0, positionInChapterMs: 0, isPlaying: false };
  const log: string[] = [];
  let status: BufferStatus = {
    state: 'idle',
    bookId: null,
    connectionType: 'unknown',
    windowMs: 0,
    bufferedAheadMs: 0,
    queued: 0,
    inFlight: null,
    evictions: 0,
    failures: 0,
    pinned: [],
    log: [],
  };
  const listeners = new Set<(status: BufferStatus) => void>();

  let running = false;
  let stopped = false;
  let wakeRequested = false;
  /** URLs given up on this session; forgotten when the open book or the pins change. */
  const failed = new Set<string>();
  /** Media found in each chapter's HTML, by chapter id; scanned once per session. */
  const discovered = new Map<string, string[]>();
  /** Store size when the cap was last hit; fetching resumes only once it drops. */
  let capBlockedAtBytes: number | null = null;
  /** Reader payloads for pinned books, obtained this session. */
  const pinnedPayloads = new Map<string, ReaderBook>();
  /** Pinned books whose payload could not be fetched; tried again after a network or pin change. */
  const payloadUnavailable = new Set<string>();
  /** Pinned books this session fetched something for, and those seen complete since — so completion is reported once. */
  const pinnedFetched = new Set<string>();
  const pinnedCompleted = new Set<string>();
  let lastSettings: OfflineSettings | null = null;
  let unsubscribeNetwork: (() => void) | null = null;
  let unsubscribeSettings: (() => void) | null = null;
  let tickHandle: unknown = null;

  const note = (line: string): void => {
    log.unshift(`${timeOfDay(deps.now())} ${line}`);
    log.splice(LOG_LINES);
    console.info(`[buffer] ${line}`);
  };

  const setStatus = (patch: Partial<Omit<BufferStatus, 'log'>>): void => {
    status = { ...status, ...patch, log: [...log] };
    for (const listener of listeners) {
      try {
        listener(status);
      } catch (error) {
        console.warn('[buffer] A status listener threw.', error);
      }
    }
  };

  const bookInputs = () => {
    const { book, voiceKey, currentChapterIndex, positionInChapterMs } = inputs;
    return book && voiceKey ? { book, voiceKey, currentChapterIndex, positionInChapterMs } : null;
  };

  const depthNow = (): number => {
    const current = bookInputs();
    return current ? bufferedAheadMs({ ...current, isHeld: deps.store.hasAsset }) : 0;
  };

  const pinnedBooksNow = (): readonly PinnedBook[] => deps.settings.getOfflineSettings().pinnedBooks;

  /** The open book and every pinned book: never evicted. */
  const protectedBookIds = (pins: readonly PinnedBook[]): Set<string> => {
    const ids = new Set(pins.map((pin) => pin.bookId));
    if (inputs.book) {
      ids.add(inputs.book.id);
    }
    return ids;
  };

  const inUseAssetUrls = (): string[] => {
    const current = bookInputs();
    if (!current) {
      return [];
    }
    const { book, voiceKey } = current;
    const playing = Math.min(Math.max(0, Math.floor(current.currentChapterIndex)), book.chapters.length - 1);
    const urls = new Set<string>();
    for (let index = playing - IN_USE_CHAPTER_RADIUS; index <= playing + IN_USE_CHAPTER_RADIUS; index += 1) {
      const chapter = book.chapters[index];
      if (!chapter) {
        continue;
      }
      for (const ref of listChapterAssets(book, chapter, voiceKey)) {
        // Only the chapter that is playing has its audio open; a
        // neighbour's is looked up afresh when its turn comes.
        if (ref.kind !== 'audio' || index === playing) {
          urls.add(ref.cdnUrl);
        }
      }
      for (const cdnUrl of discovered.get(chapter.id) ?? []) {
        urls.add(cdnUrl);
      }
    }
    return Array.from(urls);
  };

  // --- pinned books --------------------------------------------------------

  /** The pin's voice, or the book's default if the payload no longer offers it. */
  const pinnedVoice = (book: ReaderBook, pin: PinnedBook): string =>
    book.readingVoices.some((voice) => voice.voiceKey === pin.voiceKey) ? pin.voiceKey : book.defaultVoiceKey;

  /** The whole book: the window walk runs to the last chapter, and text is never limited. */
  const planWholeBook = (book: ReaderBook, voiceKey: string): PlannedFetch[] =>
    planBufferFetches({
      book,
      voiceKey,
      currentChapterIndex: 0,
      positionInChapterMs: 0,
      windowMs: Number.POSITIVE_INFINITY,
      eagerText: true,
      discoveredMedia: discovered,
      isHeld: deps.store.hasAsset,
    });

  /**
   * A pinned book's payload without going to the network: the open book's
   * if it is that book, else one obtained earlier this session, else the
   * reader cache. Synchronous, so progress can be reported while offline.
   */
  const knownPayload = (pin: PinnedBook): ReaderBook | null => {
    if (inputs.book?.id === pin.bookId) {
      return inputs.book;
    }
    const known = pinnedPayloads.get(pin.bookId);
    if (known) {
      return known;
    }
    const cached = deps.readerBooks.getCached(pin.bookId);
    if (cached) {
      pinnedPayloads.set(pin.bookId, cached);
    }
    return cached;
  };

  /**
   * The payload, fetching it if nothing at hand has it: one fetch per
   * session. A failed fetch is not retried until the network or the pins
   * change, so an unreachable book cannot turn the loop into a request storm.
   */
  const pinnedPayload = async (pin: PinnedBook): Promise<ReaderBook | null> => {
    const known = knownPayload(pin);
    if (known) {
      return known;
    }
    if (payloadUnavailable.has(pin.bookId)) {
      return null;
    }
    try {
      const fetched = await deps.readerBooks.fetch(pin.bookId);
      pinnedPayloads.set(pin.bookId, fetched);
      return fetched;
    } catch (error) {
      payloadUnavailable.add(pin.bookId);
      note(`pinned ${pin.bookId}: could not fetch the book (${errorText(error)}); will try again later`);
      return null;
    }
  };

  /** Derived from what the store holds, for every pinned book whose payload is at hand. */
  const pinnedProgress = (pins: readonly PinnedBook[]): PinnedBookProgress[] => {
    const progress: PinnedBookProgress[] = [];
    let stored: StoredAsset[] | null = null;
    for (const pin of pins) {
      const book = knownPayload(pin);
      if (!book) {
        continue;
      }
      const voiceKey = pinnedVoice(book, pin);
      stored ??= deps.store.listStoredAssets();
      const bytes = stored.reduce((sum, asset) => sum + (asset.bookId === pin.bookId ? asset.bytes : 0), 0);
      let chaptersDone = 0;
      for (const chapter of book.chapters) {
        const held =
          listChapterAssets(book, chapter, voiceKey).every((ref) => deps.store.hasAsset(ref.cdnUrl)) &&
          (discovered.get(chapter.id) ?? []).every((cdnUrl) => deps.store.hasAsset(cdnUrl));
        if (held) {
          chaptersDone += 1;
        }
      }
      const plan = planWholeBook(book, voiceKey);
      progress.push({
        bookId: pin.bookId,
        voiceKey,
        chaptersDone,
        chaptersTotal: book.chapters.length,
        bytes,
        complete: plan.length === 0,
        blocked: plan.filter((planned) => failed.has(planned.ref.cdnUrl)).length,
      });
    }
    return progress;
  };

  /** Say so, once, when a book this session fetched for has nothing left to fetch. */
  const reportCompletions = (pins: readonly PinnedBook[], progress: readonly PinnedBookProgress[]): void => {
    for (const entry of progress) {
      if (!entry.complete || pinnedCompleted.has(entry.bookId) || !pinnedFetched.has(entry.bookId)) {
        continue;
      }
      pinnedCompleted.add(entry.bookId);
      const pin = pins.find((candidate) => candidate.bookId === entry.bookId);
      note(`pinned ${entry.bookId}: complete, ${formatBytes(entry.bytes)} on disk`);
      deps.track('Book Download Completed', {
        book_id: entry.bookId,
        bytes: entry.bytes,
        chapters: entry.chaptersTotal,
        // Since the pin was made, so it may span sessions; a download is
        // allowed to finish long after it was asked for.
        duration_ms: pin ? Math.max(0, deps.now() - pin.pinnedAt) : null,
      });
    }
  };

  // --- fetching ------------------------------------------------------------

  const withWatchdog = (promise: Promise<StoredAsset>, cdnUrl: string): Promise<FetchOutcome> =>
    new Promise((resolve) => {
      let settled = false;
      let timer: unknown = null;
      const finish = (outcome: FetchOutcome) => {
        if (settled) return;
        settled = true;
        deps.timers.clear(timer);
        unsubscribe();
        resolve(outcome);
      };
      const arm = () => {
        deps.timers.clear(timer);
        timer = deps.timers.set(() => finish({ kind: 'stalled' }), deps.watchdogMs);
      };
      const unsubscribe = deps.store.subscribeToAssetProgress((progress) => {
        if (progress.cdnUrl === cdnUrl) arm();
      });
      arm();
      promise.then(
        (asset) => finish({ kind: 'stored', asset }),
        (error: unknown) => finish({ kind: 'error', error }),
      );
    });

  const discoverMedia = async (book: ReaderBook, chapterIndex: number): Promise<void> => {
    const chapter = book.chapters[chapterIndex];
    if (!chapter || discovered.has(chapter.id)) {
      return;
    }
    const html = await deps.readHeldText(chapter.htmlUrl);
    if (html === null) {
      return;
    }
    const urls = collectChapterMediaUrls(html, chapter.htmlUrl);
    discovered.set(chapter.id, urls);
    if (urls.length > 0) {
      note(`chapter ${chapterIndex}: ${urls.length} media file${urls.length === 1 ? '' : 's'} to fetch`);
    }
  };

  /**
   * Chapters whose HTML was fetched in an earlier session have media nobody
   * has looked for yet this session. Look now, so it joins the plan.
   */
  const scanHeldHtml = async (book: ReaderBook): Promise<void> => {
    for (const [chapterIndex, chapter] of book.chapters.entries()) {
      if (stopped) {
        return;
      }
      if (discovered.has(chapter.id) || !deps.store.hasAsset(chapter.htmlUrl)) {
        continue;
      }
      await discoverMedia(book, chapterIndex);
      if (!discovered.has(chapter.id)) {
        // Unreadable; do not look again.
        discovered.set(chapter.id, []);
      }
    }
  };

  const tryEvict = async (protectedIds: ReadonlySet<string>): Promise<string | null> => {
    // Like every other removal, eviction leaves alone what the player and
    // the reader are using. That is not only the open book's own files: the
    // app's reader stylesheets are shared by every book and filed under
    // whichever fetched them first, which may well be the victim.
    const inUse = new Set(inUseAssetUrls());
    // Only a book with something that can actually go is worth evicting.
    // One left holding nothing but files in use would free no room, stay a
    // candidate, and be picked again on the retry, for ever.
    const bookIds = new Set(
      deps.store.listStoredAssets().filter((asset) => !inUse.has(asset.cdnUrl)).map((asset) => asset.bookId),
    );
    for (const bookId of protectedIds) {
      bookIds.delete(bookId);
    }
    const candidates = Array.from(bookIds)
      .filter((bookId) => deps.isBookFinished(bookId))
      .sort((a, b) => deps.lastPlayedAt(a) - deps.lastPlayedAt(b));
    const victim = candidates[0];
    if (!victim) {
      return null;
    }
    await deps.store.purgeBook(victim, { keepUrls: inUse });
    return victim;
  };

  const fetchOne = async (planned: PlannedFetch, work: Work, connectionType: ConnectionType, pins: readonly PinnedBook[]): Promise<void> => {
    const { ref, chapterIndex, tier } = planned;
    const { book } = work;
    const label = work.pin ? `pinned ${book.id}: ` : '';
    const startedAt = deps.now();
    setStatus({ inFlight: { cdnUrl: ref.cdnUrl, kind: ref.kind, bookId: book.id, chapterIndex, startedAt } });

    const outcome = await withWatchdog(
      deps.store.putAsset({ cdnUrl: ref.cdnUrl, bookId: book.id, voiceKey: ref.voiceKey, expectedBytes: ref.expectedBytes }),
      ref.cdnUrl,
    );
    const elapsedMs = deps.now() - startedAt;

    if (outcome.kind === 'stored') {
      note(`${label}${ref.kind} for chapter ${chapterIndex} (${tier}): ${formatBytes(outcome.asset.bytes)} in ${elapsedMs} ms`);
      // Whichever plan asked for it: a short pinned book may be fetched
      // entirely by the open book's window, and it still completes.
      if (work.pin || pins.some((pin) => pin.bookId === book.id)) {
        pinnedFetched.add(book.id);
      }
      if (ref.kind === 'html') {
        await discoverMedia(book, chapterIndex);
      }
      if (ref.kind === 'audio') {
        deps.track('Buffer Chapter Buffered', {
          book_id: book.id,
          chapter_index: chapterIndex,
          tier,
          pinned: work.pin !== null,
          bytes: outcome.asset.bytes,
          duration_ms: elapsedMs,
          connection_type: connectionType,
          buffered_ahead_minutes: Math.round(depthNow() / 60_000),
        });
      }
      setStatus({ inFlight: null });
      return;
    }

    if (outcome.kind === 'stalled') {
      failed.add(ref.cdnUrl);
      note(`${label}${ref.kind} for chapter ${chapterIndex}: no bytes for ${Math.round(deps.watchdogMs / 1000)} s, giving up on it this session`);
      deps.track('Buffer Fetch Stalled', { book_id: book.id, kind: ref.kind, chapter_index: chapterIndex, pinned: work.pin !== null });
      setStatus({ inFlight: null, failures: status.failures + 1 });
      return;
    }

    const { error } = outcome;
    if (isAssetStoreError(error, 'superseded')) {
      // The book was purged while this was downloading and the store threw
      // the file away. Not a failure of the asset: the next plan decides
      // afresh whether it is still wanted.
      note(`${label}${ref.kind} for chapter ${chapterIndex}: discarded, the book was cleared while it was downloading`);
      setStatus({ inFlight: null });
      return;
    }
    if (isAssetStoreError(error, 'cap')) {
      const evicted = await tryEvict(protectedBookIds(pins));
      if (evicted) {
        note(`store full; evicted finished book ${evicted} and will retry`);
        deps.track('Buffer Cap Reached', { book_id: book.id, used_bytes: deps.store.getStoreUsage().bytes, evicted: true });
        setStatus({ inFlight: null, evictions: status.evictions + 1 });
      } else {
        capBlockedAtBytes = deps.store.getStoreUsage().bytes;
        note('store full and nothing finished to evict; streaming from here');
        deps.track('Buffer Cap Reached', { book_id: book.id, used_bytes: capBlockedAtBytes, evicted: false });
        setStatus({ inFlight: null });
      }
      return;
    }

    failed.add(ref.cdnUrl);
    const errorKind = isAssetStoreError(error) ? error.kind : 'unknown';
    note(`${label}${ref.kind} for chapter ${chapterIndex} failed (${errorKind}): ${errorText(error)}`);
    deps.track('Buffer Fetch Failed', {
      book_id: book.id,
      kind: ref.kind,
      chapter_index: chapterIndex,
      pinned: work.pin !== null,
      error_kind: errorKind,
      http_status: isAssetStoreError(error) ? error.httpStatus ?? null : null,
    });
    setStatus({ inFlight: null, failures: status.failures + 1 });
  };

  // --- the loop ------------------------------------------------------------

  /** The first pinned book, in pin order, with anything left to fetch. */
  const nextPinnedWork = async (pins: readonly PinnedBook[]): Promise<Work | null> => {
    for (const pin of pins) {
      if (stopped) {
        return null;
      }
      const book = await pinnedPayload(pin);
      if (!book) {
        continue;
      }
      await scanHeldHtml(book);
      const plan = planWholeBook(book, pinnedVoice(book, pin)).filter((planned) => !failed.has(planned.ref.cdnUrl));
      if (plan.length > 0) {
        return { book, plan, pin };
      }
    }
    return null;
  };

  const loop = async (): Promise<void> => {
    running = true;
    try {
      while (!stopped) {
        wakeRequested = false;
        const current = bookInputs();
        const pins = pinnedBooksNow();
        const bookId = current?.book.id ?? null;
        if (!current && pins.length === 0) {
          setStatus({ state: 'idle', bookId: null, windowMs: 0, bufferedAheadMs: 0, queued: 0, inFlight: null, pinned: [] });
          return;
        }
        const network = await deps.network.getNetworkState();
        const depth = depthNow();
        if (!network.isOnline) {
          setStatus({ state: 'offline', bookId, connectionType: 'none', windowMs: 0, bufferedAheadMs: depth, queued: 0, inFlight: null, pinned: pinnedProgress(pins) });
          return;
        }
        if (network.connectionType === 'cellular' && !deps.settings.getOfflineSettings().cellularBuffering) {
          // The user's call, not the policy's: nothing at all over mobile data.
          setStatus({ state: 'cellular-off', bookId, connectionType: 'cellular', windowMs: 0, bufferedAheadMs: depth, queued: 0, inFlight: null, pinned: pinnedProgress(pins) });
          return;
        }

        const windowMs = current ? lookAheadWindowMs(network) : 0;
        let work: Work | null = null;
        if (current) {
          await scanHeldHtml(current.book);
          const plan = planBufferFetches({
            ...current,
            windowMs,
            eagerText: isUnmetered(network),
            discoveredMedia: discovered,
            isHeld: deps.store.hasAsset,
          }).filter((planned) => !failed.has(planned.ref.cdnUrl));
          if (plan.length > 0) {
            work = { book: current.book, plan, pin: null };
          }
        }
        if (!work) {
          work = await nextPinnedWork(pins);
        }

        const progress = pinnedProgress(pins);
        reportCompletions(pins, progress);
        const common = { bookId, connectionType: network.connectionType, windowMs, bufferedAheadMs: depth, pinned: progress };

        if (!work) {
          setStatus({ ...common, state: 'window-full', queued: 0, inFlight: null });
          return;
        }
        if (capBlockedAtBytes !== null) {
          if (deps.store.getStoreUsage().bytes >= capBlockedAtBytes) {
            setStatus({ ...common, state: 'cap-reached', queued: work.plan.length, inFlight: null });
            return;
          }
          capBlockedAtBytes = null;
          note('room appeared in the store; fetching again');
        }

        setStatus({ ...common, state: 'fetching', queued: work.plan.length });
        await fetchOne(work.plan[0], work, network.connectionType, pins);
      }
    } catch (error) {
      // Nothing in here should throw, but a bug must not take the loop down
      // silently and leave the buffer flat for the rest of the session.
      note(`scheduler error: ${errorText(error)}`);
      setStatus({ state: 'idle', inFlight: null });
    } finally {
      running = false;
      if (wakeRequested && !stopped) {
        wake();
      }
    }
  };

  const wake = (): void => {
    if (stopped || !deps.isNative()) {
      return;
    }
    if (running) {
      wakeRequested = true;
      return;
    }
    void loop();
  };

  // --- subscriptions -------------------------------------------------------

  const clearTick = () => {
    if (tickHandle !== null) {
      deps.timers.clear(tickHandle);
      tickHandle = null;
    }
  };

  const scheduleTick = () => {
    clearTick();
    if (!inputs.book || stopped) {
      return;
    }
    tickHandle = deps.timers.set(() => {
      tickHandle = null;
      // The window rolls only while the playhead moves; a paused book has
      // nothing new to want, and a cap check is cheap either way.
      if (inputs.isPlaying || capBlockedAtBytes !== null) {
        wake();
      }
      scheduleTick();
    }, deps.tickMs);
  };

  const onSettingsChange = () => {
    const next = deps.settings.getOfflineSettings();
    const previous = lastSettings;
    lastSettings = next;
    if (!previous || previous.cellularBuffering !== next.cellularBuffering) {
      note(`mobile data: ${next.cellularBuffering ? 'allowed' : 'not allowed'}`);
    }
    if (!previous || pinsKey(previous) !== pinsKey(next)) {
      const pinnedIds = new Set(next.pinnedBooks.map((pin) => pin.bookId));
      note(pinnedIds.size > 0 ? `pinned: ${Array.from(pinnedIds).join(', ')}` : 'pinned: none');
      // A change to the pins is the user asking again: anything given up on
      // gets another chance, and a book pinned anew reports completion anew.
      failed.clear();
      payloadUnavailable.clear();
      for (const bookId of Array.from(pinnedPayloads.keys())) {
        if (!pinnedIds.has(bookId)) {
          pinnedPayloads.delete(bookId);
        }
      }
      for (const bookId of Array.from(pinnedCompleted)) {
        if (!pinnedIds.has(bookId)) {
          pinnedCompleted.delete(bookId);
          pinnedFetched.delete(bookId);
        }
      }
    }
    ensureSubscriptions();
    wake();
  };

  const ensureSubscriptions = () => {
    if (!unsubscribeSettings) {
      // Always, from the first use: a pin made with no book open must wake
      // the loop, and the switch is read on every turn regardless.
      lastSettings = deps.settings.getOfflineSettings();
      unsubscribeSettings = deps.settings.subscribeToOfflineSettings(onSettingsChange);
    }
    const hasWork = !!inputs.book || pinnedBooksNow().length > 0;
    if (hasWork && !unsubscribeNetwork) {
      unsubscribeNetwork = deps.network.subscribeToNetworkState((state) => {
        note(`connection: ${state.isOnline ? state.connectionType : 'offline'}`);
        // A payload that could not be fetched may be reachable now.
        payloadUnavailable.clear();
        wake();
      });
    } else if (!hasWork && unsubscribeNetwork) {
      unsubscribeNetwork();
      unsubscribeNetwork = null;
    }
    if (inputs.book) {
      if (tickHandle === null) {
        scheduleTick();
      }
    } else {
      clearTick();
    }
  };

  // --- public --------------------------------------------------------------

  const update = (next: BufferInputs): void => {
    const bookChanged = next.book?.id !== inputs.book?.id || next.voiceKey !== inputs.voiceKey;
    inputs = next;
    if (bookChanged) {
      failed.clear();
      capBlockedAtBytes = null;
      if (next.book) {
        note(`book ${next.book.id}, voice ${next.voiceKey}`);
      }
    }
    ensureSubscriptions();
    wake();
  };

  const stop = (): void => {
    stopped = true;
    unsubscribeNetwork?.();
    unsubscribeNetwork = null;
    unsubscribeSettings?.();
    unsubscribeSettings = null;
    clearTick();
    listeners.clear();
  };

  return {
    update,
    poke: wake,
    inUseAssetUrls,
    getStatus: () => status,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    stop,
  };
};

const catalogBook = (bookId: string) => readCachedCatalog()?.find((book) => book.id === bookId) ?? null;

export const defaultBufferSchedulerDeps: BufferSchedulerDeps = {
  isNative: isNativeApp,
  store: { putAsset, hasAsset, getStoreUsage, listStoredAssets, purgeBook, subscribeToAssetProgress },
  network: { getNetworkState, subscribeToNetworkState },
  settings: { getOfflineSettings, subscribeToOfflineSettings },
  readerBooks: {
    getCached: getCachedReaderBook,
    // Nobody is waiting on this one, so a 426 must not put the upgrade screen
    // up over a book that is playing off disk. See FetchReaderBookOptions.
    fetch: (bookId) => fetchReaderBook(bookId, undefined, { blockOnUpgradeRequired: false }),
  },
  readHeldText: async (cdnUrl) => {
    const local = resolveAssetUrl(cdnUrl);
    if (local === cdnUrl) {
      return null;
    }
    try {
      const response = await fetch(local);
      return response.ok ? await response.text() : null;
    } catch {
      return null;
    }
  },
  // A book the catalog does not know cannot be judged finished, so it is
  // never evicted: the safe direction.
  isBookFinished: (bookId) => {
    const book = catalogBook(bookId);
    return book !== null && isBookFinished(book);
  },
  lastPlayedAt: (bookId) => {
    const book = catalogBook(bookId);
    return book ? getAudioProgressForBook(book)?.lastPlayed ?? 0 : 0;
  },
  track: (event, properties) => analytics.track(event, properties),
  now: () => Date.now(),
  timers: {
    set: (callback, ms) => window.setTimeout(callback, ms),
    clear: (handle) => window.clearTimeout(handle as number),
  },
  watchdogMs: 45_000,
  tickMs: 30_000,
};

/** The one scheduler the app runs. */
export const bufferScheduler: BufferScheduler = createBufferScheduler(defaultBufferSchedulerDeps);
